import { isEqual } from "lodash-es";
import { AddressData, CheckoutData, IntegrationsType } from "ecom-types";

import { cartApi } from "@/api";
import {
  paymentMethods,
  stripePaymentMethodNames,
  supportedCountriesList,
  defaultCountry,
  defaultCurrency,
  // countryCurrencyMap,
} from "@/configs";

enum paytomorrowReturn {
  success = "success",
  cancel = "cancel",
}
enum allowedQueryParams {
  payTomorrowReturn = "paytomorrow-return",
  orderId = "order_id",
  billingZip = "billing_zip",
  email = "email",
  cartUniqueId = "cart_unique_id",
}

export const useCheckout = () => {
  const router = useRouter();
  const route = useRoute();
  const generalStore = useGeneralStore();
  const checkoutStore = useCheckoutStore();
  const showApplePayButton = ref(false);
  const paymentLoading = ref(false);
  const isOrdering = ref(false);
  const isPaypalInit = ref(false);
  const config = useRuntimeConfig();

  const { openSystemDialog } = useSystemDialogs();
  const { t } = useI18n();

  const { hasOnlyDigitalItemsInCart } = useCart();

  const { checkoutErrorEvent } = useEvents();

  const { sendLog } = useLogger();

  // Helper function to check if country is supported
  const isCountrySupported = (countryCode: string): boolean => {
    return supportedCountriesList.includes(countryCode as any);
  };

  // Helper function to get currency for country
  // const getCurrencyForCountry = (countryCode: string): string => {
  //   return countryCurrencyMap[countryCode as keyof typeof countryCurrencyMap] || defaultCurrency;
  // };

  const country = defaultCountry;
  const currency = defaultCurrency;

  const paypalIntegration =
    generalStore.bootstrap?.integrations?.[IntegrationsType.TYPE_PAYPAL];

  const paypalPublicKey =
    paypalIntegration && paypalIntegration.mode === "live"
      ? paypalIntegration.live?.client_id || ""
      : paypalIntegration?.sandbox?.client_id || "";

  const elemetsErrors = ref({
    cardNumberElement: "",
    cardExpiryElement: "",
    cardCvcElement: "",
  });

  const skipPayment = computed(() => {
    return Number(generalStore?.cart?.final_amount) < 1;
  });

  function forceStripeLoading() {
    if (checkoutStore.stripe?.source) {
      return;
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.onload = () => {
        generalStore.setStripeCDNLoaded(true);
        resolve(true);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function forcePaypalLoading() {
    if (window?.paypal) {
      return;
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${paypalPublicKey}`;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function createAffirmMessageElement(price: number) {
    const appearance = {
      variables: {
        colorText: "#000",
        fontSizeBase: "12px",
        fontFamily: "Montserrat, sans-serif",
      },
    };
    const fonts = [
      {
        cssSrc:
          "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
      },
    ];
    const elements = checkoutStore.stripe.source.elements({
      appearance,
      fonts,
    });
    const options = {
      amount: Math.round(price),
      currency: "USD",
      countryCode: "US",
      paymentMethodTypes: ["affirm"],
    };

    const PaymentMessageElement = elements.create(
      "paymentMethodMessaging",
      options,
    );
    try {
      PaymentMessageElement.mount("#payment-method-messaging-element");
    } catch (err) {
      console.error('mount("#payment-method-messaging-element") error');
      checkoutErrorEvent('mount("#payment-method-messaging-element") error');
    }
  }

  async function initStripeSource() {
    if (checkoutStore.stripe.source) {
      return;
    }
    const publicKey =
      generalStore.bootstrap?.integrations?.[
        IntegrationsType.TYPE_STRIPE_AFFIRM
      ]?.public_key;
    if (!publicKey) {
      console.error("STRIPE_PUBLIC_KEY error");
      checkoutErrorEvent("STRIPE_PUBLIC_KEY error");
      generalStore.setGlobalPaymentLoading(false);
      paymentLoading.value = false;
      return;
    }
    if (!(window as any).Stripe) {
      try {
        await forceStripeLoading();
      } catch (e) {
        console.error("Stripe script loading failed");
        checkoutErrorEvent("Stripe script loading failed");
        paymentLoading.value = false;
        generalStore.setGlobalPaymentLoading(false);
      }
    }

    if (!checkoutStore.stripe?.source) {
      checkoutStore.stripe.source = (window as any)?.Stripe(publicKey);
    }
  }

  async function initStripe(options: Record<string, any>) {
    generalStore.setGlobalPaymentLoading(true);
    await initStripeSource();

    // const applePayGateway = options?.payment_method_types?.payment_gateways
    //   ?.find((el) => el.title === paymentMethods.stripe)
    //   ?.payment_methods?.find((el) => el.title === paymentMethods.applePay);

    // const googlePayGateway = options?.payment_method_types?.payment_gateways
    //   ?.find((el) => el.title === paymentMethods.stripe)
    //   ?.payment_methods?.find((el) => el.title === paymentMethods.googlePay);
    // const applePayGateway =
    //   options?.payment_method_types?.payment_gateways?.find(
    //     (el: Record<string, any>) => el.code === paymentMethods.applePay,
    //   );
    //
    // const googlePayGateway =
    //   options?.payment_method_types?.payment_gateways?.find(
    //     (el: Record<string, any>) => el.code === paymentMethods.googlePay,
    //   );

    // if (applePayGateway?.status || googlePayGateway?.status) {
    //   await initStripeApplePay({
    //     applePayGateway,
    //     googlePayGateway,
    //     style: options?.style,
    //   });

    // }
    if (
      checkoutStore.applePayOptions?.status ||
      checkoutStore?.googlePayOptions?.status ||
      checkoutStore.amazonOptions?.status ||
      checkoutStore.stripePaypalOptions?.status
    ) {
      console.log("initStripeExpressCheckoutElement");
      initStripeExpressCheckoutElement();
    }

    if (options.payment_method_types.card) {
      initStripeElements();
    }
    await checkStripeExternalPayment();
    generalStore.setGlobalPaymentLoading(false);
  }

  function initStripeElements() {
    const elements = checkoutStore.stripe.source.elements({
      locale: "en",
      fonts: [
        {
          family: "GT Ultra",
          src: `url(${config.public.domain}fonts/GT-Ultra-Standard-Light.woff)`,
          weight: "300",
        },
      ],
    });
    const style = {
      base: {
        color: "#303333",
        // width: "100%",
        fontSize: "16px",
        fontWeight: "300",
        fontFamily: '"GT Ultra", sans-serif',
        // transition: "all 0.2s ease",
        "::placeholder": {
          color: "transparent",
          fontWeight: "300",
        },
      },
      invalid: {
        color: "#000",
        iconColor: "#000",
      },
    };
    const cardNumberElement = elements.create("cardNumber", {
      style,
      placeholder: "Card number",
    });
    checkoutStore.stripe.cardNumberElement = cardNumberElement;

    cardNumberElement.mount("#card-number-element");
    checkoutStore.stripe.cardNumberElement.on("change", (event: any) => {
      if (event.error) {
        console.log("error - ", event.error.message);
        checkoutErrorEvent(event.error.message);
        paymentLoading.value = false;
        elemetsErrors.value.cardNumberElement = event.error.message;
      } else {
        elemetsErrors.value.cardNumberElement = "";
      }
    });

    const cardExpiryElement = elements.create("cardExpiry", {
      style,
      placeholder: "MM / YY",
    });
    checkoutStore.stripe.cardExpiryElement = cardExpiryElement;
    cardExpiryElement.mount("#card-expiry-element");
    checkoutStore.stripe.cardExpiryElement.on("change", (event: any) => {
      if (event.error) {
        elemetsErrors.value.cardExpiryElement = event.error.message;
        paymentLoading.value = false;
        checkoutErrorEvent(event.error.message);
      } else {
        elemetsErrors.value.cardExpiryElement = "";
      }
    });

    const cardCvcElement = elements.create("cardCvc", {
      style,
      placeholder: "CVC",
    });
    checkoutStore.stripe.cardCvcElement = cardCvcElement;
    cardCvcElement.mount("#card-cvc-element");
    checkoutStore.stripe.cardCvcElement.on("change", (event: any) => {
      if (event.error) {
        elemetsErrors.value.cardCvcElement = event.error.message;
        paymentLoading.value = false;
        checkoutErrorEvent(event.error.message);
      } else {
        elemetsErrors.value.cardCvcElement = "";
      }
    });
  }

  function initStripeExpressCheckoutElement() {
    if (!generalStore?.cart?.final_amount) {
      return;
    }
    const expressCheckoutOptions = {
      mode: "payment",
      amount: Math.round(Number(generalStore?.cart?.final_amount) * 100),
      currency,
    };

    const elements = checkoutStore.stripe.source.elements(
      expressCheckoutOptions,
    );

    const expressCheckoutElementOptions = {
      paymentMethods: {
        amazonPay: checkoutStore.amazonOptions?.status ? "auto" : "never",
        applePay: checkoutStore.applePayOptions?.status ? "auto" : "never",
        googlePay: checkoutStore.googlePayOptions?.status ? "auto" : "never",
        link: "never",
        paypal: checkoutStore.stripePaypalOptions?.status ? "auto" : "never",
      },
      layout: {
        // maxRows: 1,
        // maxColumns: 4,
        overflow: "never",
      },
      buttonType: {
        applePay: "plain",
        googlePay: "plain",
        paypal: "paypal",
      },
      buttonHeight: 48,
    };

    const expressCheckoutElement = elements.create(
      "expressCheckout",
      expressCheckoutElementOptions,
    );

    const expressCheckoutElementContainer = document.getElementById(
      "express-checkout-element",
    );
    if (!expressCheckoutElementContainer) {
      return;
    }

    expressCheckoutElement.mount("#express-checkout-element");
    expressCheckoutElement.on("click", (event) => {
      const options = {
        emailRequired: true,
        shippingAddressRequired: !hasOnlyDigitalItemsInCart.value,
        allowedShippingCountries: supportedCountriesList,
        shippingRates: hasOnlyDigitalItemsInCart.value
          ? []
          : [
              {
                id: "default",
                displayName: "Loading ...",
                // detail: el.deliveryServiceSummary,
                amount: 0,
              },
            ],
      };
      event.resolve(options);
    });
    expressCheckoutElement.on("ready", ({ availablePaymentMethods }) => {
      if (!availablePaymentMethods) {
        showApplePayButton.value = false;
      } else {
        showApplePayButton.value = true;
      }
    });
    expressCheckoutElement.on("cancel", () => {
      elements.update({ amount: (generalStore.cart?.final_amount || 0) * 100 });
    });

    let selectedAddress: AddressData | null = null;
    expressCheckoutElement.on("shippingaddresschange", async (ev: any) => {
      // elements.update({ amount: 2000 });
      const resolve = ev.resolve;
      const reject = ev.reject;

      if (!isCountrySupported(ev.address.country)) {
        reject();
      } else {
        const address = {
          address_line_1: ev.address.city,
          address_line_2: "",
          city: ev.address.city,
          state: ev.address.state,
          country: ev.address.country,
          zip: ev.address.postal_code,
          person_first_name: "",
          phone_number: ev.address.phone,
        };

        const { data, error } = await cartApi.shippingRatesByAddress({
          cart_unique_id: generalStore.cart?.unique_id as string,
          address,
        });
        if (error.value) {
          console.log("error.value - ", error.value);
          paymentLoading.value = false;
          checkoutErrorEvent(error.value);
          reject();
        } else if (data.value) {
          const result = data.value.data;
          const {
            data: onChangeShippingMethodData,
            error: onChangeShippingMethodError,
          } = await cartApi.expressCheckoutUpdateShipping({
            cart_unique_id: generalStore.cart?.unique_id as string,
            shipping_address: address,
            shipping_method: result?.[0]?.deliveryServiceCode || "",
          });
          if (onChangeShippingMethodError.value) {
            console.log("error - ", onChangeShippingMethodError.value);
            paymentLoading.value = false;
            checkoutErrorEvent(onChangeShippingMethodError.value);
            reject();
          }

          selectedAddress = { ...address };

          elements.update({
            amount: Math.round(
              Number(onChangeShippingMethodData.value?.data.total_amount) * 100,
            ),
          });
          resolve({
            shippingRates: result?.map((el) => ({
              id: el.deliveryServiceCode,
              displayName: el.deliveryServiceName,
              // detail: el.deliveryServiceSummary,
              amount: Math.round(+el.price * 100),
            })),
          });
        }
      }
    });
    expressCheckoutElement.on("shippingratechange", async (ev: any) => {
      const resolve = ev.resolve;
      const reject = ev.reject;
      const {
        data: onChangeShippingMethodData,
        error: onChangeShippingMethodError,
      } = await cartApi.expressCheckoutUpdateShipping({
        cart_unique_id: generalStore.cart?.unique_id as string,
        shipping_address: selectedAddress as AddressData,
        shipping_method: ev?.shippingRate?.id || "",
      });
      if (onChangeShippingMethodError.value) {
        console.log("error - ", onChangeShippingMethodError.value);
        paymentLoading.value = false;
        checkoutErrorEvent(onChangeShippingMethodError.value);
        reject();
      }

      elements.update({
        amount: Math.round(
          Number(onChangeShippingMethodData.value?.data.total_amount) * 100,
        ),
      });
      resolve();
    });

    completeExpressCheckout(expressCheckoutElement, elements);
  }
  function completeExpressCheckout(expressCheckoutElement: any, elements: any) {
    expressCheckoutElement.on("confirm", async (event) => {
      generalStore.setGlobalPaymentLoading(true);
      paymentLoading.value = true;
      checkoutStore.buttonLoading = true;
      const paymentFailed = event.paymentFailed;

      if (event.shippingRate?.id === "default") {
        generalStore.setGlobalPaymentLoading(false);
        paymentLoading.value = false;
        checkoutStore.buttonLoading = false;
        paymentFailed();
        return;
      }

      const address = {
        person_first_name: event.shippingAddress?.name,
        person_last_name: "",
        email_address: event.billingDetails?.email,
        phone_number: "",
        fax_number: "",
        address_line_1: event.shippingAddress?.address?.line1,
        address_line_2: "",
        address_line_3: "",
        city: event.shippingAddress?.address?.city,
        state: event.shippingAddress?.address?.state,
        country: event.shippingAddress?.address?.country,
        zip: event.shippingAddress?.address?.postal_code,
      };

      const billingAddress = {
        person_first_name:
          event.billingDetails?.name || event.shippingAddress?.name,
        person_last_name: "",
        email_address: event.billingDetails?.email,
        phone_number: "",
        fax_number: "",
        address_line_1:
          event.billingDetails?.address?.line1 ||
          event.shippingAddress?.address?.line1,
        address_line_2: "",
        address_line_3: "",
        city:
          event.billingDetails?.address?.city ||
          event.shippingAddress?.address?.city,
        state:
          event.billingDetails?.address?.state ||
          event.shippingAddress?.address?.state,
        country:
          event.billingDetails?.address?.country ||
          event.shippingAddress?.address?.country,
        zip:
          event.billingDetails?.address?.postal_code ||
          event.shippingAddress?.address?.postal_code,
      };

      const params = {
        type: event.expressPaymentType, // affirm_express
        cart_unique_id: generalStore?.cart?.unique_id || "",
        shipping_address: address as AddressData,
        billing_address: billingAddress as AddressData,
        shipping_method: event?.shippingRate?.id || "",
      };

      const [{ data, error }] = await Promise.all([
        cartApi.startExpressPayment(params),
      ]);

      if (data.value) {
        if (data.value?.data?.order?.po_number) {
          await successCheckout(data.value?.data, params);
          return;
        }
        checkoutStore.stripe.paymentIntentClientSecret =
          data.value?.data.client_secret;
      } else if (error.value) {
        paymentLoading.value = false;
        checkoutStore.buttonLoading = false;
        generalStore.setGlobalPaymentLoading(false);
        let errorMessage = "";
        if (error?.value?.data?.errors?.length) {
          errorMessage = error?.value?.data?.errors?.[0].message;
        } else if (error.value?.data?.errors?.error) {
          errorMessage = error.value?.data?.errors?.error;
        } else if (error.value?.data) {
          errorMessage =
            (Object.values(error.value?.data).flat()?.join(" ") as string) ||
            "";
        }
        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: errorMessage,
        });
        console.log("error - ", error.value?.data);
        checkoutErrorEvent(error.value?.data);
        paymentFailed();
        return;
      }

      const { error: confirmPaymentError } =
        await checkoutStore.stripe.source.confirmPayment({
          elements,
          clientSecret: checkoutStore.stripe?.paymentIntentClientSecret,
          confirmParams: {
            return_url: `${window.location.href}?external_payment_method=${event.expressPaymentType}&cart_unique_id=${generalStore?.cart?.unique_id}&email=${checkoutStore.generalForm?.contact_email}&checkoutStep=2&paymentMethodName=${event.expressPaymentType}`,
          },
          redirect: "if_required",
        });

      if (confirmPaymentError) {
        console.log("error - ", confirmPaymentError);
        handleStripeExpressCheckoutError(confirmPaymentError);
        paymentLoading.value = false;
        checkoutStore.buttonLoading = false;
        generalStore.setGlobalPaymentLoading(false);

        // Log Stripe error
        sendLog("stripe", {
          action: "confirmPayment_express",
          cart_unique_id: generalStore?.cart?.unique_id,
          user_info: generalStore?.userInfo || null,
          request: {
            elements: "[Stripe Elements Instance]",
            clientSecret: checkoutStore?.stripe?.paymentIntentClientSecret,
            confirmParams: {
              return_url: `${window.location.href}?external_payment_method=${event?.expressPaymentType}&cart_unique_id=${generalStore?.cart?.unique_id}&email=${checkoutStore?.generalForm?.contact_email}&checkoutStep=2&paymentMethodName=${event?.expressPaymentType}`,
            },
            redirect: "if_required",
          },
          response: {
            error: {
              type: confirmPaymentError?.type,
              code: confirmPaymentError?.code,
              message: confirmPaymentError?.message,
              decline_code: confirmPaymentError?.decline_code,
              payment_intent: confirmPaymentError?.payment_intent
                ? {
                    id: confirmPaymentError?.payment_intent?.id,
                    status: confirmPaymentError?.payment_intent?.status,
                  }
                : null,
            },
          },
          message: confirmPaymentError?.message,
        });

        paymentFailed();
        // This point is reached only if there's an immediate error when confirming the payment. Show the error to your customer (for example, payment details incomplete).
      } else {
        await checkStripeExternalPayment({
          paymentIntentClientSecret:
            checkoutStore.stripe?.paymentIntentClientSecret,
          paymentFailed,
          address,
          event,
          billingAddress,
        });
      }
    });
  }

  function handleStripeExpressCheckoutError(error) {
    const messageContainer = document.querySelector(
      "#stripe-express-error-message",
    );
    if (messageContainer) {
      messageContainer.textContent = error.message;
    }
  }

  async function initStripeApplePay(options: any) {
    const disableWallets = ["link"];
    if (!options?.applePayGateway?.status) {
      disableWallets.push("applePay");
    }
    if (!options?.googlePayGateway?.status) {
      disableWallets.push("googlePay");
    }
    const paymentRequest = checkoutStore.stripe.source.paymentRequest({
      country,
      currency,
      total: {
        label: `${generalStore.settings?.company_name} Order`,
        amount: Math.round(Number(generalStore?.cart?.final_amount) * 100),
      },
      requestShipping: true,
      shippingOptions: [],
      disableWallets, // use integrations
      requestPayerName: true,
      requestPayerEmail: true,
    });
    let selectedAddress: AddressData | null = null;
    paymentRequest.on("shippingaddresschange", async (ev: any) => {
      if (!isCountrySupported(ev.shippingAddress.country)) {
        ev.updateWith({ status: "invalid_shipping_address" });
      } else {
        // Perform server-side request to fetch shipping options
        const { data, error } = await cartApi.shippingRatesByAddress({
          cart_unique_id: generalStore.cart?.unique_id as string,
          address: {
            address_line_1: ev.shippingAddress.city,
            address_line_2: "",
            city: ev.shippingAddress.city,
            state: ev.shippingAddress.region,
            country: ev.shippingAddress.country,
            zip: ev.shippingAddress.postalCode,
            person_first_name: "",
            phone_number: ev.shippingAddress.phone,
          },
        });
        if (error.value) {
          console.log("error.value - ", error.value);
          paymentLoading.value = false;
          ev.updateWith({ status: "invalid_shipping_address" });
          checkoutErrorEvent(error.value);
        } else if (data.value) {
          const result = data.value.data;

          const {
            data: onChangeShippingMethodData,
            error: onChangeShippingMethodError,
          } = await cartApi.expressCheckoutUpdateShipping({
            cart_unique_id: generalStore.cart?.unique_id as string,
            shipping_address: {
              address_line_1: ev.shippingAddress.city,
              address_line_2: "",
              city: ev.shippingAddress.city,
              state: ev.shippingAddress.region,
              country: ev.shippingAddress.country,
              zip: ev.shippingAddress.postalCode,
              person_first_name: "",
              phone_number: ev.shippingAddress.phone,
            },
            shipping_method: result?.[0]?.deliveryServiceCode,
          });
          if (onChangeShippingMethodError.value) {
            console.log("error - ", onChangeShippingMethodError.value);
            paymentLoading.value = false;
            ev.updateWith({ status: "fail" });
            checkoutErrorEvent(onChangeShippingMethodError.value);
          }

          selectedAddress = {
            address_line_1: ev.shippingAddress.city,
            address_line_2: "",
            city: ev.shippingAddress.city,
            state: ev.shippingAddress.region,
            country: ev.shippingAddress.country,
            zip: ev.shippingAddress.postalCode,
            person_first_name: "",
            phone_number: ev.shippingAddress.phone,
          };

          ev.updateWith({
            status: "success",
            shippingOptions: result?.map((el) => ({
              id: el.deliveryServiceCode,
              label: el.deliveryServiceName,
              detail: el.deliveryServiceSummary,
              amount: Math.round(+el.price * 100),
            })),
            total: {
              label: `${generalStore.settings?.company_name} Order`,
              amount: Math.round(
                Number(onChangeShippingMethodData.value?.data.total_amount) *
                  100,
              ),
            },
          });
        }
      }
    });
    paymentRequest.on("shippingoptionchange", async (ev: any) => {
      const {
        data: onChangeShippingMethodData,
        error: onChangeShippingMethodError,
      } = await cartApi.expressCheckoutUpdateShipping({
        cart_unique_id: generalStore.cart?.unique_id as string,
        shipping_address: selectedAddress as AddressData,
        shipping_method: ev?.shippingOption?.id || "",
      });
      if (onChangeShippingMethodError.value) {
        console.log("error - ", onChangeShippingMethodError.value);
        paymentLoading.value = false;
        ev.updateWith({ status: "fail" });
        checkoutErrorEvent(onChangeShippingMethodError.value);
      }
      ev.updateWith({
        status: "success",
        total: {
          label: `${generalStore.settings?.company_name} Order`,
          amount: Math.round(
            Number(onChangeShippingMethodData.value?.data.total_amount) * 100,
          ),
        },
      });
    });
    // console.log("apple pay init paymentRequest", paymentRequest);
    const elements = checkoutStore.stripe.source.elements();
    const prButton = elements.create("paymentRequestButton", {
      style: {
        paymentRequestButton: {
          height: options?.style?.height || "48px",
          borderRadius: options?.style?.borderRadius || "4px",
        },
      },
      paymentRequest,
    });
    // console.log("apple pay init elements", elements);
    // console.log("apple pay init prButton", prButton);
    const result = await paymentRequest.canMakePayment();
    // console.log("apple pay init canMakePayment result", result);
    if (result) {
      prButton.mount("#payment-request-button");
      showApplePayButton.value = true;
      // console.log("apple pay init prButton.mount finish");
    } else {
      // console.log("apple pay init prButton.mount not mounted");
      showApplePayButton.value = false;
    }
    completeApplePayOrder(paymentRequest);
  }

  function completeApplePayOrder(paymentRequest: any) {
    paymentRequest.on("paymentmethod", async (applePayEvent: any) => {
      // console.log("paymentmethod event", applePayEvent);
      if (
        paymentLoading.value ||
        skipPayment.value ||
        !applePayEvent.paymentMethod?.id
      ) {
        console.log("paymentLoading break");
        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: `Payment fail.`,
        });
        applePayEvent.complete("fail");
        const errorMessage = {
          type: "error",
          text: "Stripe init error (Apple pay)",
        };
        paymentLoading.value = false;
        console.log("errorMessage - ", errorMessage);
        checkoutErrorEvent(errorMessage);
        // this.$root.$emit('showGlobalModal', errorMessage);
        return;
      }
      paymentLoading.value = true;
      const nameArray = applePayEvent.payerName.split(" ");
      const address = {
        // address_name: applePayEvent.shippingAddress,
        email_address: applePayEvent.payerEmail,
        person_first_name: nameArray[0],
        person_last_name: nameArray.splice(1, nameArray.length - 1).join(" "),
        address_line_1: applePayEvent.shippingAddress.addressLine[0],
        address_line_2: "",
        state: applePayEvent.shippingAddress.region,
        city: applePayEvent.shippingAddress.city,
        zip: applePayEvent.shippingAddress.postalCode,
        phone_number: "",
        country: applePayEvent.shippingAddress.country,
      };

      if (!checkoutStore.generalForm) {
        checkoutStore.generalForm = {} as CheckoutData;
      }
      checkoutStore.generalForm.shipping_address = address;
      checkoutStore.generalForm.billing_address = address;
      checkoutStore.generalForm.shipping_method =
        applePayEvent?.shippingOption?.id || "";
      checkoutStore.generalForm.shipping_address_sms_notifications = false;
      console.log("applePayEvent - ", applePayEvent);
      if (!checkoutStore.generalForm?.contact_email) {
        checkoutStore.generalForm.contact_email =
          applePayEvent.payerEmail || "";
      }
      if (
        applePayEvent.methodName === "google-pay" ||
        applePayEvent.methodName.includes("google")
      ) {
        checkoutStore.generalForm.payment_method = paymentMethods.googlePay;
      }
      if (
        applePayEvent.methodName === "apple-pay" ||
        applePayEvent.methodName.includes("apple")
      ) {
        checkoutStore.generalForm.payment_method = paymentMethods.applePay;
      }

      await createOrder({
        paymentMethodId: applePayEvent.paymentMethod.id,
        approvePayment: false,
        applePayEvent,
      });
    });
  }

  async function completeOrder(payload: CheckoutData) {
    if (paymentLoading.value) {
      paymentLoading.value = false;
      return;
    }

    if (!skipPayment.value && !checkoutStore.stripe.source) {
      // this.$alert({
      //   title: `${this.$t("error")}!`,
      //   text: this.$t("stripeInitError"),
      // });
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: t("stripeInitError"),
      });
      console.log("stripeInitError");
      paymentLoading.value = false;
      checkoutErrorEvent("stripeInitError");
      return;
    }

    checkoutStore.generalForm = {
      ...checkoutStore.generalForm,
      billing_address:
        payload?.same_as_shipping_address || skipPayment.value
          ? checkoutStore.generalForm?.shipping_address
          : {
              ...payload.billing_address,
              country:
                payload?.billing_address?.country?.countryShortCode ||
                payload?.billing_address?.country,
              state:
                payload.billing_address?.state?.shortCode ||
                payload?.billing_address?.state,
            },
    };

    // delete checkoutStore.generalForm?.payment_method;
    paymentLoading.value = true;

    if (!skipPayment.value) {
      const result = await checkoutStore.stripe.source.createPaymentMethod({
        type: "card",
        // card: checkoutStore.stripe.cardElement,
        card: checkoutStore.stripe.cardNumberElement,
        billing_details: {
          email: checkoutStore.generalForm?.contact_email,
          name: payload.name_on_card,
          address: {
            city: checkoutStore.generalForm?.billing_address?.city,
            // country: checkoutStore.generalForm?.billing_address?.country,
            line1: checkoutStore.generalForm?.billing_address?.address_line_1,
            line2: checkoutStore.generalForm?.billing_address?.address_line_2,
            postal_code: checkoutStore.generalForm?.billing_address?.zip,
            // state: checkoutStore.generalForm?.billing_address?.state,
            country:
              checkoutStore.generalForm?.billing_address?.country
                ?.countryShortCode ||
              checkoutStore.generalForm?.billing_address?.country,
            state:
              checkoutStore.generalForm?.billing_address?.state?.shortCode ||
              checkoutStore.generalForm?.billing_address?.state,
          },
        },
      });

      if (!result || result.error || !result.paymentMethod?.id) {
        console.error("err payment", result.error);
        paymentLoading.value = false;
        // this.$alert({
        //   title: `${this.$t("error")}!`,
        //   text: result.error?.message
        //     ? result.error.message
        //     : this.$t("stripePaymentError"),
        // });

        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: result.error?.message
            ? result.error.message
            : t("stripePaymentError"),
        });
        console.log(
          result.error?.message ? result.error.message : "stripePaymentError",
        );
        checkoutErrorEvent(result.error.message);

        // Log Stripe error
        sendLog("stripe", {
          action: "createPaymentMethod",
          cart_unique_id: generalStore?.cart?.unique_id,
          user_info: generalStore?.userInfo || null,
          request: {
            type: "card",
            card: "[Stripe CardNumber Element]",
            billing_details: {
              email: checkoutStore?.generalForm?.contact_email,
              name: payload?.name_on_card,
              address: {
                city: checkoutStore?.generalForm?.billing_address?.city,
                line1:
                  checkoutStore?.generalForm?.billing_address?.address_line_1,
                line2:
                  checkoutStore?.generalForm?.billing_address?.address_line_2,
                postal_code: checkoutStore?.generalForm?.billing_address?.zip,
                country:
                  (checkoutStore?.generalForm?.billing_address?.country as any)
                    ?.countryShortCode ||
                  checkoutStore?.generalForm?.billing_address?.country,
                state:
                  (checkoutStore?.generalForm?.billing_address?.state as any)
                    ?.shortCode ||
                  checkoutStore?.generalForm?.billing_address?.state,
              },
            },
          },
          response: result?.error
            ? {
                error: {
                  type: result?.error?.type,
                  code: result?.error?.code,
                  message: result?.error?.message,
                  decline_code: result?.error?.decline_code,
                  param: result?.error?.param,
                },
              }
            : {
                paymentMethod: result?.paymentMethod
                  ? {
                      id: result?.paymentMethod?.id,
                      type: result?.paymentMethod?.type,
                      card: result?.paymentMethod?.card,
                    }
                  : null,
              },
          message:
            result?.error?.message || "Stripe payment method creation failed",
        });

        return;
      }

      checkoutStore.paymentMethodId = result.paymentMethod.id;
      checkoutStore.paymentMethodCard = result.paymentMethod.card;

      paymentLoading.value = false;

      return result;
    }

    return true;
  }

  async function createOrder({
    paymentMethodId,
    approvePayment,
    applePayEvent,
  }: {
    paymentMethodId: any;
    approvePayment?: any;
    applePayEvent?: any;
  }) {
    paymentLoading.value = true;

    const params = JSON.parse(JSON.stringify(checkoutStore.generalForm));

    if (paymentMethodId) {
      const paymentKey = approvePayment
        ? "payment_intent_id"
        : "payment_method_nonce";
      params[paymentKey] = paymentMethodId;
    } else if (params.payment_method === paymentMethods.paytomorrow) {
      params.no_payment_needed = false; // need to finalize cart one of keys 'payment_method_nonce', 'payment_intent_id', 'no_payment_needed', 'paypal_order_id', 'session_id'
    } else {
      params.no_payment_needed = true;
    }

    const paymentGateway = generalStore.paymentMethodsData?.find(
      (el) => el.code === checkoutStore.generalForm?.payment_method,
    );

    if (!paymentGateway && !skipPayment.value) {
      paymentLoading.value = false;
      applePayEvent.complete("fail");
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `payment fail`,
      });
      return;
    }
    if (paymentGateway) {
      params.payment_gateway_id = paymentGateway.payment_gateway_id;
      params.payment_method_id = paymentGateway.id;
    }

    delete params.payment_method;
    if (typeof params.shipping_method !== "string") {
      params.shipping_method = "";
    }
    const { data, error } = await cartApi.checkout(params);
    if (error.value) {
      paymentLoading.value = false;
      console.log("create order error", error.value);
      const errorMessage = {
        type: "error",
        text: "Server error",
      };
      checkoutErrorEvent(error.value);
      let errorTitle = `${t("error")}!`;
      if (error?.value?.data?.errors?.length) {
        if (error?.value?.data?.errors[0].additional_data?.errors?.error) {
          errorMessage.text =
            error?.value?.data?.errors[0].additional_data?.errors?.error;
        } else if (error?.value?.data?.errors?.[0]?.message) {
          errorMessage.text = error?.value?.data?.errors?.[0]?.message;
        } else if (error.value?.data?.errors?.[0]?.additional_data?.msg) {
          errorMessage.text = error.value.data.errors[0].additional_data.msg;
        }
      } else if (error.value?.data?.errors?.error) {
        errorMessage.text = error.value?.data?.errors?.error;
      }

      const errorData = error.value?.data?.errors;

      if (errorData?.requires_action) {
        const clientSecret = errorData?.payment_intent_client_secret;
        const redirectUrl = errorData?.redirect_url;
        const returnUrl = errorData?.return_url;

        if (applePayEvent) {
          console.log("Apple pay requires_action");
        } else if (redirectUrl && returnUrl) {
          window.location.href = redirectUrl;
          return;
        } else if (clientSecret) {
          const { error: errorAction, paymentIntent } =
            await checkoutStore.stripe.source.handleCardAction(
              error.value?.data?.errors?.payment_intent_client_secret,
            );
          if (!errorAction) {
            await createOrder({
              paymentMethodId: paymentIntent.id,
              approvePayment: true,
            });
            return;
          }

          // Log Stripe error
          sendLog("stripe", {
            action: "handleCardAction",
            cart_unique_id: checkoutStore?.generalForm?.cart_unique_id,
            user_info: generalStore?.userInfo || null,
            request: {
              clientSecret:
                error?.value?.data?.errors?.payment_intent_client_secret,
            },
            response: {
              error: {
                type: errorAction?.type,
                code: errorAction?.code,
                message: errorAction?.message,
                decline_code: errorAction?.decline_code,
              },
            },
            message: errorAction?.message || "Failed to verify payment method",
          });
        }
        errorMessage.text = "Failed to verify payment method.";
      }
      if (applePayEvent) {
        paymentLoading.value = false;
        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: "applePayEvent fail",
        });
        applePayEvent.complete("fail");
      }
      if (
        error.value?.data?.errors?.[0]?.code === 1 &&
        error.value?.data?.errors?.[0]?.message
      ) {
        errorMessage.text = "";
        errorTitle = t("orderCancelled");
        window.location.assign(error.value?.data?.errors?.[0]?.message);
      }
      console.log("errorMessage - ", errorMessage);
      openSystemDialog({
        type: "alert",
        title: errorTitle,
        text: errorMessage.text,
      });
      // this.$root.$emit("showGlobalModal", errorMessage);
      if (
        !generalStore?.regularCart?.unique_id ||
        params.cart_unique_id === generalStore?.regularCart?.unique_id
      ) {
        await generalStore.getCart();
      }

      paymentLoading.value = false;
    } else {
      if (data.value?.data?.paytomorrow_url) {
        window.location.replace(data.value?.data?.paytomorrow_url);
        return;
      }

      if (applePayEvent) {
        applePayEvent.complete("success");
      }
      await successCheckout(data.value?.data, params);
    }
  }

  async function successCheckout(data: any, params: any) {
    const { cartUniqueId, orderAddress } = useCookiesService();

    orderAddress.value = JSON.stringify({
      [params.cart_unique_id]: {
        billing_address: params.same_as_shipping_address
          ? params.shipping_address
          : params.billing_address,
        shipping_address: params.shipping_address,
      },
    });

    await nextTick();
    if (params.cart_unique_id === cartUniqueId.value) {
      cartUniqueId.value = data?.order?.cart_unique_id || "";
    }

    const billingAddressEmail = params?.billing_address?.email_address;
    const redirectQuery = generalStore.userInfo
      ? ""
      : `?email=${
          billingAddressEmail || checkoutStore.generalForm?.contact_email
        }&billing_zip=${
          params?.billing_address?.zip || params.shipping_address?.zip
        }`;
    await router.push(
      `/checkout/success/${params.cart_unique_id}${redirectQuery}`,
    );
    setTimeout(async () => {
      if (
        !generalStore?.regularCart?.unique_id ||
        params.cart_unique_id === generalStore?.regularCart?.unique_id
      ) {
        await generalStore.getCart();
      }

      paymentLoading.value = false;
    }, 1500); // todo
    paymentLoading.value = false;
  }

  async function saveForm() {
    const { data, error } = await cartApi.checkout(checkoutStore.generalForm!);
    if (error.value) {
      console.log(error?.response?.data?.errors);
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.value?.data?.errors[0].message,
      });
      paymentLoading.value = false;
      checkoutErrorEvent(error?.value?.data?.errors[0].message);
    } else {
      generalStore.setCart(data.value?.data || null);
      isOrdering.value = true;
    }
  }

  const paypalActions = ref<any>(null);

  async function initPaypal(validateForm: any, options?: any) {
    if (options?.data) {
      checkoutStore.generalForm = {
        ...checkoutStore.generalForm,
        ...options.data,
      };
    }
    await forcePaypalLoading();
    generalStore.setPaypalCDNLoaded(true);
    // if (!(window as any).paypal) {

    //   try {
    //     await forcePaypalLoading();
    //   } catch (e) {
    //     console.error("Paypal script loading failed");
    //   }
    // }

    let selectedShippingAddress: any = {};
    const isExpress =
      options?.expressPaymentGateway?.code === paymentMethods?.paypal_express;
    window.paypal
      .Buttons({
        fundingSource: window.paypal.FUNDING.PAYPAL,
        style: {
          height: options?.height || 48,
          color: "gold",
          // disableMaxHeight: true,
          borderRadius: options?.borderRadius,
        },
        onInit(data: any, actions: any) {
          if (validateForm) {
            actions.disable();
            paypalActions.value = actions;
          }
          isPaypalInit.value = true;
        },
        async onClick(data: any, actions: any) {
          if (validateForm) {
            const { valid } = await validateForm();

            if (!valid) {
              return actions.reject();
            } else {
              paypalActions.value?.enable();
              return actions.resolve();
            }
          }
        },
        async onShippingChange(data: any, actions: any) {
          if (isExpress && !hasOnlyDigitalItemsInCart.value) {
            if (!isCountrySupported(data.shipping_address.country_code)) {
              return actions.reject();
            }

            if (isEqual(selectedShippingAddress, data.shipping_address)) {
              if (data.selected_shipping_option?.id) {
                const {
                  data: shippingMethodResponce,
                  error: shippingMethodError,
                } = await cartApi.updatePayPalOrderShippingMethod({
                  cart_unique_id:
                    checkoutStore.generalForm?.cart_unique_id || "",
                  paypal_order_id: data.orderID,
                  shipping_method: data?.selected_shipping_option?.id,
                });
                if (shippingMethodError.value) {
                  paymentLoading.value = false;
                  return actions.reject();
                } else if (shippingMethodResponce.value) {
                  return shippingMethodResponce.value;
                }
              }
              return;
            }

            const {
              data: paypalOrderExpressData,
              error: paypalOrderExpressError,
            } = await cartApi.updatePayPalOrderShippingAddress({
              cart_unique_id: checkoutStore.generalForm?.cart_unique_id || "",
              paypal_order_id: data.orderID,
              shipping_address: {
                address_line_1: data.shipping_address.city,
                city: data.shipping_address.city,
                state: data.shipping_address.state,
                country: data.shipping_address.country_code,
                zip: data.shipping_address.postal_code,
              },
            });

            if (paypalOrderExpressError.value) {
              console.log(
                "paypalOrderExpressError  onShippingChange- ",
                paypalOrderExpressError,
              );
              paymentLoading.value = false;
              selectedShippingAddress = null;
              checkoutErrorEvent("paypalOrderExpressError  onShippingChange- ");
              return actions.reject();
            }
            selectedShippingAddress = data.shipping_address;
            return paypalOrderExpressData.value;
          }
        },
        async createOrder() {
          const paypalGateway = isExpress
            ? checkoutStore.paypalExpressOptions
            : checkoutStore.paypalOptions;

          if (!paypalGateway || !paypalGateway.status) {
            selectedShippingAddress = null;
            return;
          }
          const payload = {
            ...checkoutStore.generalForm!,
            billing_address: checkoutStore.generalForm?.shipping_address,
            payment_gateway_id: paypalGateway.payment_gateway_id,
            payment_method_id: paypalGateway.id,
          };

          delete payload.payment_method;
          if (isExpress) {
            const { data: paymentData, error: paymentError } =
              await cartApi.updatePaymentMethod({
                cart_unique_id: generalStore.cart?.unique_id || "",
                payment_method_id: paypalGateway.id,
              });
            if (paymentData.value) {
              const { data, error } = await cartApi.paypalOrder({
                cart_unique_id: checkoutStore.generalForm?.cart_unique_id || "",
              });
              if (error.value) {
                paymentLoading.value = false;
                console.log(error.value);
                selectedShippingAddress = null;
                checkoutErrorEvent(error.value);
                openSystemDialog({
                  type: "alert",
                  title: `${t("error")}!`,
                  text:
                    error.value.data?.errors?.[0]?.message || t("paymentError"),
                });
                return false;
              }
              return data.value?.data?.id;
            } else if (paymentError.value) {
              paymentLoading.value = false;
              console.log("paymentError - ", paymentError);
              checkoutErrorEvent(paymentError.value);
              openSystemDialog({
                type: "alert",
                title: `${t("error")}!`,
                text:
                  paymentError.value.data?.errors?.[0]?.message ||
                  t("paymentError"),
              });
            }
          } else {
            const { error } = await cartApi.checkout(payload);
            if (!error.value) {
              const { data, error } = await cartApi.paypalOrder({
                cart_unique_id: checkoutStore.generalForm?.cart_unique_id || "",
              });
              if (error.value) {
                paymentLoading.value = false;
                selectedShippingAddress = null;
                console.log(error.value);
                checkoutErrorEvent(error.value);
                openSystemDialog({
                  type: "alert",
                  title: `${t("error")}!`,
                  text:
                    error.value.data?.errors?.[0]?.message || t("paymentError"),
                });
              }
              return data.value?.data?.id;
            } else {
              openSystemDialog({
                type: "alert",
                title: `${t("error")}!`,
                text:
                  error.value.data?.errors?.[0]?.message || t("paymentError"),
              });
            }
          }
        },
        async onApprove(data: any) {
          const paypalGateway = isExpress
            ? checkoutStore.paypalExpressOptions
            : checkoutStore.paypalOptions;
          if (!paypalGateway || !paypalGateway.status) {
            return;
          }
          let payload = {};

          if (isExpress) {
            const { data: orderDetails, error: orderError } =
              await cartApi.getPaypalOrderExpress(
                generalStore.cart?.unique_id || "",
                data.orderID,
              );
            if (orderError.value) {
              console.log("error - ", orderError.value);
              paymentLoading.value = false;
              selectedShippingAddress = null;
              checkoutErrorEvent(orderError.value);
              openSystemDialog({
                type: "alert",
                title: `${t("error")}!`,
                text:
                  orderError.value.data?.errors?.[0]?.message ||
                  t("orderError"),
              });
              return;
            } else if (orderDetails.value?.data.purchase_units[0]) {
              const address = {
                address_line_1:
                  orderDetails.value?.data.purchase_units[0]?.shipping.address
                    ?.address_line_1,
                city: orderDetails.value?.data.purchase_units[0].shipping
                  .address?.admin_area_2,
                state:
                  orderDetails.value?.data.purchase_units[0].shipping.address
                    ?.admin_area_1,
                country:
                  orderDetails.value?.data.purchase_units[0].shipping.address
                    ?.country_code,
                zip: orderDetails.value?.data.purchase_units[0].shipping.address
                  ?.postal_code,
                email_address: orderDetails.value.data.payer.email_address,
                person_first_name:
                  orderDetails.value.data.payer.name.given_name,
                person_last_name: orderDetails.value.data.payer.name.surname,
              };
              payload = {
                ...checkoutStore.generalForm,
                billing_address: address,
                shipping_address: address,
                payment_gateway_id: paypalGateway.payment_gateway_id,
                payment_method_id: paypalGateway.id,
                paypal_order_id: data.orderID,
                shipping_method: hasOnlyDigitalItemsInCart.value
                  ? ""
                  : orderDetails.value.data.purchase_units[0].shipping
                      .options[0].id,
                contact_email: orderDetails.value.data.payer.email_address,
                shipping_address_sms_notifications: false,
              };

              if (checkoutStore.generalForm) {
                checkoutStore.generalForm.contact_email =
                  orderDetails.value.data.payer.email_address;
              }
            } else {
              console.log("error - No purchase_units ");
              checkoutErrorEvent("error - No purchase_units ");
            }
          } else {
            payload = {
              ...checkoutStore.generalForm,
              billing_address: checkoutStore.generalForm
                ?.same_as_shipping_address
                ? checkoutStore.generalForm?.shipping_address || undefined
                : {
                    ...checkoutStore.generalForm?.billing_address,
                    country:
                      checkoutStore.generalForm?.billing_address?.country
                        ?.countryShortCode || "",
                    state:
                      checkoutStore.generalForm?.billing_address?.state
                        ?.shortCode || "",
                  },
              payment_gateway_id: paypalGateway.payment_gateway_id,
              payment_method_id: paypalGateway.id,
              paypal_order_id: data.orderID,
            };
          }

          delete payload.payment_method;
          const { data: orderData, error } = await cartApi.checkout(payload);

          if (error.value) {
            selectedShippingAddress = null;
            paymentLoading.value = false;
            console.log(
              "error.value - ",
              error.value?.data.errors?.[0]?.message,
            );
            checkoutErrorEvent(error.value?.data.errors?.[0]?.message);
            if (error.value?.data.errors?.[0]?.message) {
              openSystemDialog({
                type: "alert",
                title: `${t("error")}!`,
                text: error.value?.data?.errors?.[0]?.message || "",
              });
            }
            return;
          }

          const element = document.getElementById("paypal-button-container");
          if (element) {
            element.innerHTML = "<h3>Thank you for your payment!</h3>";
          }
          await successCheckout(orderData.value?.data, payload);
        },
        onCancel: function (data: any) {
          console.log("onCancel");
          console.log(data);
        },
        onError: function (err: any) {
          paymentLoading.value = false;
          console.log("onError");
          console.log(err);
          checkoutErrorEvent(err);
          // console.log(err.response);
          // console.log(err.message);
          /* openSystemDialog({
            type: "alert",
            title: `${t("error")}!`,
            text: err.message,
          }); */
        },
      })
      .render(options.el);
  }

  async function applyStripeExternalPayment(paymentMethodName: string) {
    if (!paymentMethodName) {
      return;
    }
    router.replace({
      path: route.path,
      query: {
        checkoutStep: route.query?.checkoutStep || "",
      },
    });
    // this.paymentLoading = true;
    await getStripeClientSecret(paymentMethodName);
    await initStripeExternalPayment(paymentMethodName);
    // this.paymentLoading = false;
  }

  async function getStripeClientSecret(paymentMethodName: string) {
    // if (this.billing) {
    //   this.form.billing_address = this.form.shipping_address;
    // }
    const params = {
      type: paymentMethodName, // affirm_express
      cart_unique_id: generalStore?.cart?.unique_id || "",
      shipping_address: checkoutStore.generalForm
        ?.shipping_address as AddressData,
      billing_address: checkoutStore.generalForm?.same_as_shipping_address
        ? checkoutStore.generalForm?.shipping_address
        : (checkoutStore.generalForm?.billing_address as AddressData),
      shipping_method: checkoutStore.generalForm?.shipping_method || "",
    };
    // await saveForm();

    if (checkoutStore.generalForm?.payment_method === paymentMethods.affirm) {
      checkoutStore.generalForm.payment_method_id =
        checkoutStore.affirmOptions?.id || undefined;
    } else if (
      checkoutStore.generalForm?.payment_method === paymentMethods.klarna
    ) {
      checkoutStore.generalForm.payment_method_id =
        checkoutStore.klarnaOptions?.id || undefined;
    }

    const [{ data, error }] = await Promise.all([
      cartApi.startExpressPayment(params),
      saveForm(),
    ]);

    if (data.value) {
      if (data.value?.data?.order?.po_number) {
        const payload = {
          ...checkoutStore.generalForm,
          billing_address: checkoutStore.generalForm?.same_as_shipping_address
            ? checkoutStore.generalForm?.shipping_address || undefined
            : {
                ...checkoutStore.generalForm?.billing_address,
                country:
                  checkoutStore.generalForm?.billing_address?.country
                    ?.countryShortCode || "",
                state:
                  checkoutStore.generalForm?.billing_address?.state
                    ?.shortCode || "",
              },
        };
        await successCheckout(data.value?.data, payload);
      }
      checkoutStore.stripe.paymentIntentClientSecret =
        data.value?.data.client_secret;
    } else if (error.value) {
      paymentLoading.value = false;
      checkoutStore.buttonLoading = false;
      let errorMessage = "";
      if (error?.value?.data?.errors?.length) {
        errorMessage = error?.value?.data?.errors?.[0].message;
      } else if (error.value?.data?.errors?.error) {
        errorMessage = error.value?.data?.errors?.error;
      } else if (error.value?.data) {
        errorMessage =
          (Object.values(error.value?.data).flat()?.join(" ") as string) || "";
      }
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: errorMessage,
      });
      console.log("error - ", error.value?.data);
      checkoutErrorEvent(error.value?.data);
    }
  }

  async function initStripeExternalPayment(paymentMethodName: string) {
    const confirmMethodName = stripePaymentMethodNames[paymentMethodName];

    if (!checkoutStore.stripe.paymentIntentClientSecret || !confirmMethodName) {
      return;
    }
    try {
      const shippingAdress = (type: string) => {
        return {
          name: `${checkoutStore.generalForm?.[type].person_first_name} ${checkoutStore.generalForm?.[type].person_last_name}`,
          address: {
            line1: checkoutStore.generalForm?.[type].address_line_1,
            city: checkoutStore.generalForm?.[type].city,
            state: checkoutStore.generalForm?.[type].state,
            country: checkoutStore.generalForm?.[type].country,
            postal_code: checkoutStore.generalForm?.[type].zip,
          },
        };
      };
      const paymentOptions = {
        payment_method: {
          billing_details: {
            email: checkoutStore.generalForm?.contact_email,
            name: checkoutStore.generalForm?.same_as_shipping_address
              ? shippingAdress("shipping_address").name
              : shippingAdress("billing_address").name,
            address: checkoutStore.generalForm?.same_as_shipping_address
              ? shippingAdress("shipping_address").address
              : shippingAdress("billing_address").address,
          },
        },
        shipping: {
          name: shippingAdress("shipping_address").name,
          address: shippingAdress("shipping_address").address,
        },
        // return_url: `${window.location.href}&external_payment_method=${paymentMethodName}&cart_unique_id=${generalStore?.cart?.cart?.unique_id}&email=${checkoutStore.generalForm?.contact_email}`,
        return_url: `${window.location.href}?external_payment_method=${paymentMethodName}&cart_unique_id=${generalStore?.cart?.unique_id}&email=${checkoutStore.generalForm?.contact_email}&checkoutStep=2&paymentMethodName=${paymentMethodName}`,
      };

      setOnpageshowEventAfterRedirect();

      const paymentResult = await checkoutStore.stripe.source[
        confirmMethodName
      ](checkoutStore.stripe?.paymentIntentClientSecret, paymentOptions);

      if (paymentResult?.error) {
        paymentLoading.value = false;
        checkoutStore.buttonLoading = false;
        console.log("paymentResult error", paymentResult.error);
        checkoutErrorEvent(paymentResult.error);

        // Log Stripe error
        sendLog("stripe", {
          action: `confirm_${paymentMethodName}`,
          cart_unique_id: generalStore?.cart?.unique_id,
          user_info: generalStore?.userInfo || null,
          request: {
            clientSecret: checkoutStore?.stripe?.paymentIntentClientSecret,
            payment_method: {
              billing_details: paymentOptions?.payment_method?.billing_details,
            },
            shipping: paymentOptions?.shipping,
            return_url: paymentOptions?.return_url,
          },
          response: {
            error: {
              type: paymentResult?.error?.type,
              code: paymentResult?.error?.code,
              message: paymentResult?.error?.message,
              decline_code: paymentResult?.error?.decline_code,
              payment_intent: paymentResult?.error?.payment_intent
                ? {
                    id: paymentResult?.error?.payment_intent?.id,
                    status: paymentResult?.error?.payment_intent?.status,
                  }
                : null,
            },
          },
          message:
            paymentResult?.error?.message ||
            `${paymentMethodName} payment failed`,
        });

        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: paymentResult.error?.message || `${t("paymentError")}!`,
        });
        // this.showPopupError('Payment error');
      }
    } catch (err) {
      paymentLoading.value = false;
      checkoutStore.buttonLoading = false;
      console.log(`Stripe ${paymentMethodName} error`, err);
      checkoutErrorEvent(err);
      // this.showPopupError('Payment error');
    }
  }

  function setOnpageshowEventAfterRedirect() {
    window.onpageshow = (event) => {
      if (event.persisted) {
        console.log("paymentLoading = false;");
        checkoutStore.buttonLoading = false;
        paymentLoading.value = false;
      }
    };
  }

  async function checkStripeExternalPayment(expressStripeData?: any) {
    if (
      !route.query.payment_intent_client_secret &&
      !expressStripeData?.paymentIntentClientSecret
    ) {
      generalStore.setGlobalPaymentLoading(false);
      return;
    }
    try {
      const { error, paymentIntent } =
        await checkoutStore.stripe.source.retrievePaymentIntent(
          expressStripeData?.paymentIntentClientSecret ||
            route.query?.payment_intent_client_secret,
        );

      if (error) {
        paymentLoading.value = false;
        generalStore.setGlobalPaymentLoading(false);
        checkoutStore.buttonLoading = false;
        console.log("checkStripeExternalPayment error", error);
        checkoutErrorEvent(error);
        if (expressStripeData?.paymentFailed) {
          expressStripeData.paymentFailed();
        }
        return;
        // this.showPopupError('Payment error');
      }
      if (paymentIntent?.status === "succeeded") {
        generalStore.setGlobalPaymentLoading(true);
        const { data, error } = await cartApi.startExpressPayment({
          cart_unique_id:
            (route.query.cart_unique_id as string) ||
            (generalStore.cart?.unique_id as string),
          type:
            (route.query.paymentMethodName as string) ||
            expressStripeData?.event?.expressPaymentType,
          shipping_address:
            expressStripeData?.address ||
            (checkoutStore.generalForm?.shipping_address as AddressData),
          billing_address:
            expressStripeData?.billingAddress ||
            expressStripeData?.address(
              checkoutStore.generalForm?.billing_address as AddressData,
            ),
        });
        if (data.value) {
          await successCheckout(data.value?.data, {
            cart_unique_id:
              (route.query.cart_unique_id as string) ||
              (generalStore.cart?.unique_id as string),
            shipping_address:
              expressStripeData?.address ||
              (checkoutStore.generalForm?.shipping_address as AddressData),
            billing_address:
              expressStripeData?.billingAddress ||
              expressStripeData?.address ||
              (checkoutStore.generalForm?.billing_address as AddressData),
          });
          paymentLoading.value = false;
          generalStore.setGlobalPaymentLoading(false);
          checkoutStore.buttonLoading = false;
        } else if (error.value) {
          paymentLoading.value = false;
          generalStore.setGlobalPaymentLoading(false);
          checkoutStore.buttonLoading = false;
          console.log("error - ", error.value);

          checkoutErrorEvent(error.value);
          if (expressStripeData?.paymentFailed) {
            expressStripeData?.paymentFailed();
          }
        }
      } else if (paymentIntent?.status === "requires_confirmation") {
        const { data, error } = await cartApi.checkout({
          ...checkoutStore.generalForm,
          payment_intent_id: paymentIntent.id,
        });

        if (data.value) {
          await successCheckout(data.value?.data, {
            cart_unique_id:
              (route.query.cart_unique_id as string) ||
              (generalStore.cart?.unique_id as string),
            shipping_address:
              expressStripeData?.address ||
              (checkoutStore.generalForm?.shipping_address as AddressData),
            billing_address:
              expressStripeData?.billingAddress ||
              expressStripeData?.address ||
              (checkoutStore.generalForm?.billing_address as AddressData),
          });
          paymentLoading.value = false;
          generalStore.setGlobalPaymentLoading(false);
          checkoutStore.buttonLoading = false;
        } else if (error.value) {
          console.log("create order error", error.value);
          const errorMessage = {
            type: "error",
            text: "Server error",
          };
          const errorTitle = `${t("error")}!`;
          if (error?.value?.data?.errors?.length) {
            if (error?.value?.data?.errors[0].additional_data?.errors.error) {
              errorMessage.text =
                error?.value?.data?.errors[0].additional_data?.errors.error;
            } else if (error?.value?.data?.errors?.[0].message) {
              errorMessage.text = error?.value?.data?.errors?.[0].message;
            } else if (error.value?.data?.errors?.[0]?.additional_data?.msg) {
              errorMessage.text =
                error.value.data.errors[0].additional_data.msg;
            }
          } else if (error.value?.data?.errors?.error) {
            errorMessage.text = error.value?.data?.errors?.error;
          }
          console.log("errorMessage - ", errorMessage);
          openSystemDialog({
            type: "alert",
            title: errorTitle,
            text: errorMessage.text,
          });

          paymentLoading.value = false;
          generalStore.setGlobalPaymentLoading(false);
          checkoutStore.buttonLoading = false;
          console.log("error - ", error.value);

          checkoutErrorEvent(error.value);
          if (expressStripeData?.paymentFailed) {
            expressStripeData?.paymentFailed();
          }
        }
      } else {
        // Log Stripe error - invalid payment status
        sendLog("stripe", {
          action: "retrievePaymentIntent_invalid_status",
          cart_unique_id:
            (route?.query?.cart_unique_id as string) ||
            generalStore?.cart?.unique_id,
          user_info: generalStore?.userInfo || null,
          request: {
            clientSecret:
              expressStripeData?.paymentIntentClientSecret ||
              route?.query?.payment_intent_client_secret,
          },
          response: {
            paymentIntent: {
              id: paymentIntent?.id,
              status: paymentIntent?.status,
              amount: paymentIntent?.amount,
              currency: paymentIntent?.currency,
              payment_method: paymentIntent?.payment_method,
              last_payment_error: paymentIntent?.last_payment_error || null,
            },
          },
          message: `Payment canceled - invalid status: ${paymentIntent?.status}`,
        });

        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: "Payment canceled",
        });
        paymentLoading.value = false;
        generalStore.setGlobalPaymentLoading(false);
        checkoutStore.buttonLoading = false;
        if (expressStripeData?.paymentFailed) {
          expressStripeData?.paymentFailed();
        }
        router.replace({
          path: route.path,
          query: {
            checkoutStep: route.query?.checkoutStep || "",
          },
        });
        checkoutErrorEvent("Payment canceled");
      }
    } catch (err) {
      // Log Stripe error - retrievePaymentIntent exception
      const errorObj = err as any;
      sendLog("stripe", {
        action: "retrievePaymentIntent_error",
        cart_unique_id:
          (route?.query?.cart_unique_id as string) ||
          generalStore?.cart?.unique_id,
        user_info: generalStore?.userInfo || null,
        request: {
          clientSecret:
            expressStripeData?.paymentIntentClientSecret ||
            (route?.query?.payment_intent_client_secret as string),
        },
        response: {
          error: {
            type: errorObj?.type,
            code: errorObj?.code,
            message: errorObj?.message,
            stack: errorObj?.stack,
          },
        },
        message: errorObj?.message || "Payment verification failed",
      });

      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: "Payment error",
      });
      console.log("error - ", err);
      checkoutErrorEvent(err);
      paymentLoading.value = false;
      generalStore.setGlobalPaymentLoading(false);
      checkoutStore.buttonLoading = false;
      if (expressStripeData?.paymentFailed) {
        expressStripeData?.paymentFailed();
      }
    }
  }

  async function successPayTomorrowCheckout() {
    console.log("successPayTomorrowCheckout");
    const { cartUniqueId } = useCookiesService();

    await nextTick();
    const cartUniqueIdQuery = route.query[allowedQueryParams.cartUniqueId];
    if (cartUniqueIdQuery === cartUniqueId.value) {
      cartUniqueId.value = "";
    }
    const billingAddressEmail = route.query[allowedQueryParams.email];
    const billingZip = route.query[allowedQueryParams.billingZip];
    const redirectQuery = generalStore.userInfo
      ? ""
      : `?email=${billingAddressEmail}&billing_zip=${billingZip}`;
    console.log("before push");
    await router.push(`/checkout/success/${cartUniqueIdQuery}${redirectQuery}`);
    // generalStore.setGlobalPaymentLoading(false);
    console.log("get cart");
    if (cartUniqueIdQuery === generalStore?.regularCart?.unique_id) {
      await generalStore.getCart();
    }
    paymentLoading.value = false;
  }

  async function checkPayTomorrowPayment() {
    if (
      route.query[allowedQueryParams.payTomorrowReturn] ===
      paytomorrowReturn.success
    ) {
      paymentLoading.value = true;
      generalStore.setGlobalPaymentLoading(true);
      console.log("checkPayTomorrowPayment");
      return await successPayTomorrowCheckout();
    } else if (
      route.query[allowedQueryParams.payTomorrowReturn] ===
      paytomorrowReturn.cancel
    ) {
      openSystemDialog({
        type: "alert",
        title: t("orderCancelled"),
        text: t(
          "orderWasCancelledBecauseALoanApplicationHadBeenCancelledInPayTomorrow",
        ),
      });
    }
  }

  return {
    initStripe,
    showApplePayButton,
    skipPayment,
    isOrdering,
    completeOrder,
    saveForm,
    elemetsErrors,
    initPaypal,
    paypalActions,
    applyStripeExternalPayment,
    createOrder,
    initStripeSource,
    createAffirmMessageElement,
    checkPayTomorrowPayment,
    isPaypalInit,
  };
};
